import * as React from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { ExternalLink, LogOut, Menu } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useOwnVendor } from '@/hooks/useVendors'
import { useOrganization } from '@/hooks/useOrganization'
import { accountLabelOf } from '@/auth/access'
import { isVendor } from '@/auth/roles'
import { activeNavPath, navEntriesFor, type NavEntry } from '@/components/appNav'
import { Tooltip } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { SupportModeBanner } from '@/components/SupportModeBanner'
import { BillingBanner } from '@/components/billing/BillingBanner'
import { EmailConfirmationBanner } from '@/components/EmailConfirmationBanner'
import { VenueSwitcher } from '@/components/VenueSwitcher'
import { BrandLockup } from '@/components/BrandLockup'
import { VenueContextHero } from '@/components/VenueContextHero'
import { ThemeMenuButton } from '@/components/ThemeMenu'
import { cn } from '@/lib/utils'

/** Nav entries for the current role; the decision itself lives in appNav. */
function useNavEntries(): NavEntry[] {
  const { user } = useAuth()
  const vendorUser = isVendor(user)
  // Only a vendor user needs their vendor loaded — to know whether to show the
  // Furniture library (gated on offering the `furniture` service).
  const ownVendor = useOwnVendor({ enabled: vendorUser })
  const offersFurniture = ownVendor.data ? ownVendor.data.services.includes('furniture') : undefined
  // Already loaded for anyone who runs a company; it says which features are on.
  const features = useOrganization().data?.features ?? []
  return navEntriesFor(user, {
    features,
    ...(offersFurniture === undefined ? {} : { vendorOffersFurniture: offersFurniture }),
  })
}

/**
 * The brand mark, linking home: the wordmark on desktop, the icon on mobile.
 *
 * The real artwork rather than type set to look like it. It used to spell the
 * name in text — "CSE EVENTS" for tenant users, the product's own name for
 * platform admins, a deliberate difference about whose software you were looking
 * at. The lockup says the same thing to everyone, which is now the answer
 * everywhere else too: the browser tab, the sign-in page and every email header
 * name the product regardless of who is reading.
 *
 * The sidebar takes the short lockup — the mark and FLOH — because the full one
 * needs 240px for VENUES to be legible and the sidebar has 208 inside its
 * padding. It is the reversed file in both themes, because the sidebar is
 * blackwood in both.
 *
 * The mobile bar takes the solid mark alone, the kit's answer below 64px; the
 * reversed one, since the bar is blackwood too.
 */
function Wordmark({ compact }: { compact?: boolean }) {
  return (
    <Link
      to="/app"
      aria-label="Home"
      // `shrink-0` because this sits beside a nav that wants room: as a flex
      // item the lockup was letting itself be squeezed to 75px, half the
      // documented minimum. The nav scrolls; the mark does not shrink.
      className="inline-flex shrink-0 rounded-sm outline-none focus-visible:focus-ds"
    >
      {compact ? (
        // Decorative: the link carries the label, and the icon is the whole of
        // it. No radius utility — the tile draws its own corners in the artwork.
        <img src="/brand/mark-dark.svg" alt="" width={28} height={28} />
      ) : (
        <BrandLockup lockup="short" ground="dark" width={120} />
      )}
    </Link>
  )
}

/**
 * Plan View's nav item, on the blackwood sidebar. The current page is a raised
 * panel — a lighter forest, a gold rule down its leading edge, its icon in gold —
 * and everything else is muted until hovered. Gold rather than the action's
 * forest, which would vanish on this ground, and never the action color anyway:
 * where you are is not something to click. Icons at 18px.
 */
const sidebarItem = ({ isActive }: { isActive: boolean }): string =>
  cn(
    'flex h-[38px] items-center gap-2.5 rounded-md px-2.5 text-sm font-medium outline-none transition-colors focus-visible:focus-ds [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0',
    isActive
      ? 'bg-sidebar-active text-sidebar-ink shadow-[inset_2px_0_0_var(--ds-sidebar-active-ink)] [&>svg]:text-sidebar-active-ink'
      : 'text-sidebar-ink-muted hover:bg-sidebar-hover hover:text-sidebar-ink',
  )

/** A person's photograph or initials, on the sage support pair. */
function Avatar({ name, src }: { name: string; src: string | null | undefined }) {
  return src ? (
    <img src={src} alt="" className="h-8 w-8 shrink-0 rounded-round object-cover" />
  ) : (
    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-round bg-sage-soft text-xs font-semibold text-sage-ink">
      {initials(name)}
    </div>
  )
}

/** The icon buttons in the sidebar footer and the mobile bar. */
const shellIconButton =
  'grid h-[30px] w-[30px] place-items-center rounded-md text-sidebar-ink-muted outline-none transition-colors hover:bg-sidebar-hover hover:text-sidebar-ink focus-visible:focus-ds'

/**
 * The nav itself, rendered the same way in the desktop sidebar and in the mobile
 * tray — one list, so an entry cannot appear in one and not the other, and the
 * external-link case is not written twice.
 *
 * `onNavigate` is how the tray closes: a route change leaves a Radix dialog open
 * on its own, and the tray has done its job the moment something is chosen.
 */
function NavList({ entries, onNavigate }: { entries: NavEntry[]; onNavigate?: () => void }) {
  // Which entry is current is decided once, for the whole list, rather than by
  // each link about itself — `NavLink` can only answer "does the URL start with
  // mine", which two nested entries both answer yes to. See `activeNavPath`.
  const { pathname } = useLocation()
  const active = activeNavPath(pathname, entries)
  return (
    <>
      {entries.map((e) =>
        e.external ? (
          <a
            key={e.to}
            href={e.to}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className={sidebarItem({ isActive: false })}
          >
            {e.icon}
            <span className="flex-1">{e.label}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
          </a>
        ) : (
          // A plain `Link`, so the highlight and `aria-current` come from the
          // same decision. `NavLink` computes its own and would have announced
          // a different page to a screen reader than the one shown as selected.
          <Link
            key={e.to}
            to={e.to}
            onClick={onNavigate}
            className={sidebarItem({ isActive: e.to === active })}
            {...(e.to === active ? { 'aria-current': 'page' as const } : {})}
          >
            {e.icon}
            {e.label}
          </Link>
        ),
      )}
    </>
  )
}

/**
 * The terms and the privacy policy, from inside the app. Only the sign-in page
 * linked them, so anyone already signed in (or invited, and never shown that
 * page) had no way to read what they had agreed to.
 */
function LegalLinks({ onNavigate }: { onNavigate?: () => void }) {
  const link = 'rounded-sm outline-none hover:text-sidebar-ink hover:underline focus-visible:focus-ds'
  return (
    <p className="px-1 pb-3 text-xs text-sidebar-ink-muted">
      <Link to="/terms" className={link} onClick={onNavigate}>
        Terms of service
      </Link>
      {' · '}
      <Link to="/privacy" className={link} onClick={onNavigate}>
        Privacy policy
      </Link>
    </p>
  )
}

/** Two-letter monogram from a name or email local-part. */
function initials(nameOrEmail: string): string {
  const base = nameOrEmail.includes('@') ? (nameOrEmail.split('@')[0] ?? '') : nameOrEmail
  const parts = base.split(/[.\s_+-]+/).filter(Boolean)
  const derived = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('')
  return (derived || base[0]?.toUpperCase() || '?').slice(0, 2)
}

/**
 * The authenticated app shell, on Plan View's: a stone sidebar (brand lockup,
 * where you are, nav, you) on desktop and a compact top bar on mobile, with the
 * active route on the page ground beside it.
 */
export function AppLayout() {
  const { user, logout } = useAuth()
  const entries = useNavEntries()
  const [navOpen, setNavOpen] = React.useState(false)
  // Staff read their company role's name, as the company wrote it.
  const roleLabel = accountLabelOf(user)

  // The page scrolls, not a box inside it.
  //
  // This was a viewport-locked shell — `h-svh` with `overflow-hidden`, and only
  // `main` scrolling — which gives a list its own scrollbar inside the window's.
  // Two scrollbars on one page is a puzzle every time: the wheel does different
  // things depending on where the pointer is, the keyboard's Home/End depend on
  // what has focus, and a browser's find-in-page scrolls one of them while you
  // watch the other.
  //
  // So the document scrolls, once, and the sidebar is `sticky` instead of tall:
  // it stays against the top of the viewport with its own full height. Until the
  // page has scrolled past a banner, though, the sidebar starts a banner-height
  // down and ends that far below the fold — so the profile footer is sticky to
  // the bottom as well, and is on screen from the first paint.
  return (
    <div className="flex min-h-svh flex-col bg-page">
      <div className="shrink-0">
        <SupportModeBanner />
        <BillingBanner />
        <EmailConfirmationBanner />
      </div>
      <div className="flex flex-1">
        {/* Desktop sidebar — pinned to the viewport while the page moves under it. */}
        <aside className="sticky top-0 hidden h-svh w-[var(--ds-sidebar-width)] shrink-0 flex-col self-start border-r border-sidebar-line bg-sidebar text-sidebar-ink md:flex">
          <div className="shrink-0 px-5 pb-5 pt-[22px]">
            <Wordmark />
          </div>
          <VenueSwitcher />
          <nav className="grid min-h-0 flex-1 content-start gap-0.5 overflow-y-auto px-3">
            <NavList entries={entries} />
          </nav>
          {user && (
            <div className="sticky bottom-0 shrink-0 bg-sidebar px-3">
              <div className="flex items-center gap-1 border-t border-sidebar-line pb-3 pt-3.5">
                <NavLink to="/profile" className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-0.5 py-0.5 outline-none focus-visible:focus-ds">
                  <Avatar name={user.name || user.email} src={user.avatarUrl} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-control font-semibold text-sidebar-ink">{user.name || user.email}</p>
                    <p className="truncate text-xs leading-4 text-sidebar-ink-muted">{roleLabel}</p>
                  </div>
                </NavLink>
                <ThemeMenuButton />
                <Tooltip content="Sign out">
                  <button
                    type="button"
                    onClick={logout}
                    aria-label="Sign out"
                    className={shellIconButton}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>
              <LegalLinks />
            </div>
          )}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/*
            Mobile top bar — sticky, since there is no sidebar to hold the nav.

            The nav is behind the menu button rather than in the bar. It used to
            be a row of links that scrolled sideways, which on a 360px phone
            showed about one and a half of them with no sign that the rest were
            there: a scroller with no scrollbar reads as a nav with two items.
            The tray shows all of them, at the size a thumb wants, and it is the
            pattern the design system's own shell documents.
          */}
          <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-sidebar-line bg-sidebar px-4 md:hidden">
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetTrigger
                aria-label="Menu"
                className={cn(shellIconButton, '-ml-1.5')}
              >
                <Menu className="h-5 w-5" />
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[var(--ds-sidebar-width)] max-w-[85vw] bg-sidebar"
              >
                {/* The tray is wide enough for the wordmark, so a phone gets the
                    full mark here even though the bar only has room for the icon. */}
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="shrink-0 px-5 pb-5 pt-[22px]">
                  <BrandLockup lockup="short" ground="dark" width={120} />
                </div>
                <VenueSwitcher />
                <nav className="grid min-h-0 flex-1 content-start gap-0.5 overflow-y-auto px-3">
                  <NavList entries={entries} onNavigate={() => setNavOpen(false)} />
                </nav>
                {user && (
                  <div className="mx-3 shrink-0 border-t border-sidebar-line pb-3 pt-3.5">
                    {/* The only way to /profile on a phone: the sidebar's footer
                        link is desktop-only, so before this there was none. */}
                    <NavLink
                      to="/profile"
                      onClick={() => setNavOpen(false)}
                      className="flex min-w-0 items-center gap-2 rounded-md px-0.5 py-0.5 outline-none focus-visible:focus-ds"
                    >
                      <Avatar name={user.name || user.email} src={user.avatarUrl} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-control font-semibold text-sidebar-ink">{user.name || user.email}</p>
                        <p className="truncate text-xs leading-4 text-sidebar-ink-muted">{roleLabel}</p>
                      </div>
                    </NavLink>
                    <LegalLinks onNavigate={() => setNavOpen(false)} />
                  </div>
                )}
              </SheetContent>
            </Sheet>
            <Wordmark compact />
            <div className="ml-auto flex shrink-0 items-center gap-0.5">
              <ThemeMenuButton />
              <button
                type="button"
                onClick={logout}
                aria-label="Sign out"
                className={shellIconButton}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </header>

          {/* Which venue this session is changing, on every page inside a venue.
              It scrolls away with the page now; the sidebar's venue switcher is
              what keeps the answer on screen. */}
          <VenueContextHero />

          {/* Padding only — each page sets its own body width via <PageContainer>. */}
          <main className="w-full flex-1 px-6 py-7 md:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}
